import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantsService from '@services/ParticipantsService';
import FilesService from '@services/FilesService';
import { renameFileToUploadPattern } from '@components/UserProfileModal/UploadImageModal/helpers';
import ProfileCompletionModalView from './ProfileCompletionModalView';
import { useProfileCompletionModalState } from './context';
import { isAllowedProfileImageFile } from './helpers';
import { IUser } from '@interfaces/user';
import { IUserAvatar } from '@interfaces/avatar';
import { IUpdateProfileData } from '@interfaces/profile';
import { IMytaverseFile } from '@interfaces/file';
import ROUTES from '@constants/routes';
import { getAiAssistantLanguageOptions } from '@helpers/assistant';

/** What went wrong with the picked picture, if anything. */
export type ProfileImageError = 'unsupported' | 'uploadFailed';

/**
 * Blocks the app until the profile carries a name and a gender, then persists
 * both (`required` mode), and doubles as the dismissible profile editor opened
 * from the profile drawer, where the edits are held locally and written by an
 * explicit Save (`edit` mode).
 */
const ProfileCompletionModal = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, setUser, appSettings, selectLanguage, selectedLanguage } =
    useMytaverse();
  const { open, mode, openRequired, close } = useProfileCompletionModalState();
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [pickedImage, setPickedImage] = useState<IMytaverseFile | null>(null);
  const [imageError, setImageError] = useState<ProfileImageError | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const aiAssistant = appSettings?.aiAssistant;

  const languageOptions = getAiAssistantLanguageOptions(aiAssistant);

  useEffect(() => {
    // The gate owns the `required` flow only. An edit session is opened by the
    // user from the profile drawer and must never be torn down here — the
    // profile is complete by then, so this effect would close it instantly.
    if (open && mode === 'edit') {
      return;
    }

    const isProfileIncomplete =
      user &&
      (!user.name?.trim() ||
        !user.gender ||
        !['male', 'female'].includes(user.gender));

    if (isProfileIncomplete) {
      setName(user.name?.trim() || '');
      setGender(user.gender || '');
      openRequired();
    } else if (open) {
      close();
    }
  }, [user, open, mode, openRequired, close]);

  const wasEditOpenRef = useRef(false);

  useEffect(() => {
    const isEditOpen = open && mode === 'edit';

    // Every edit session starts from the stored profile: an abandoned one must
    // not leave its dropped edits behind for the next open.
    if (isEditOpen && !wasEditOpenRef.current) {
      setName(user?.name?.trim() || '');
      setGender(user?.gender || '');
      setPickedImage(null);
      setImageError(null);
      setIsUploading(false);
      setSaveFailed(false);
    }

    wasEditOpenRef.current = isEditOpen;
  }, [open, mode, user]);

  const trimmedName = name.trim();
  const savedName = (user?.name || '').trim();
  const isGenderPicked = gender === 'male' || gender === 'female';
  const isGenderChanged = isGenderPicked && gender !== user?.gender;

  const isDirty =
    !!pickedImage ||
    isGenderChanged ||
    (!!trimmedName && trimmedName !== savedName);

  /**
   * Uploads the picked picture and shows it right away, still unsaved.
   *
   * The file goes up on pick rather than on Save so the user sees what they
   * chose before committing to it. It lands under `uploads/`, a prefix the
   * assets bucket expires on a lifecycle rule, and only a Save moves it to its
   * permanent location — an abandoned edit leaves nothing behind to clean up.
   *
   * @param file - File picked in the input or dropped on the avatar.
   */
  const handleImagePick = useCallback(
    async (file: File) => {
      if (!isAllowedProfileImageFile(file)) {
        setImageError('unsupported');
        return;
      }

      setImageError(null);
      setSaveFailed(false);
      setIsUploading(true);

      try {
        const [uploaded] = await FilesService.uploadFiles([
          renameFileToUploadPattern(file),
        ]);

        if (!uploaded) {
          setImageError('uploadFailed');
          return;
        }

        setPickedImage({
          location: uploaded.location,
          url: uploaded.url,
          contentType: uploaded.contentType,
        });
      } catch (e) {
        console.error(`Failed to upload the profile picture: ${e}`, {
          participantId: user?.id,
        });
        setImageError('uploadFailed');
      } finally {
        setIsUploading(false);
      }
    },
    [user?.id],
  );

  /**
   * Writes the edited fields in one patch and mirrors them into the app-wide
   * user, then closes. Only what actually changed is sent; a failed write keeps
   * the modal open so the edits are not lost.
   *
   * `profileImage` is taken from the response rather than from the patch: the
   * backend moves the uploaded file to its permanent location, so the local
   * object still points at the temporary one.
   */
  const handleSave = useCallback(async () => {
    if (isSaving || isUploading || !trimmedName) {
      return;
    }

    const patch: IUpdateProfileData = {
      ...(trimmedName !== savedName ? { name: trimmedName } : {}),
      ...(isGenderChanged ? { gender } : {}),
      ...(pickedImage ? { profileImage: pickedImage } : {}),
    };

    if (!Object.keys(patch).length) {
      close();
      return;
    }

    try {
      setIsSaving(true);
      setSaveFailed(false);

      const updated = await ParticipantsService.updateCurrentParticipantProfile(
        patch,
      );

      // The gender-based default avatar is resolved server-side and does not
      // come back in the PATCH response, so the profile is re-read after a
      // gender change. Kept in its own try: the field is already saved at
      // this point and a failed re-read must not surface as a save error.
      let defaultAvatar: IUserAvatar | null | undefined;

      if (isGenderChanged) {
        try {
          const profile =
            await ParticipantsService.getCurrentParticipantProfile();

          defaultAvatar = profile?.defaultAvatar ?? null;
        } catch (e) {
          console.error(
            `Failed to re-read profile after a gender change, default avatar stays unresolved: ${e}`,
            { participantId: user?.id },
          );
        }
      }

      setUser((prevState: IUser) => ({
        ...prevState,
        ...patch,
        ...(updated?.profileImage
          ? { profileImage: updated.profileImage }
          : {}),
        ...(defaultAvatar !== undefined && { defaultAvatar }),
      }));

      close();
    } catch (e) {
      console.error('Error updating profile:', e);
      setSaveFailed(true);
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    isUploading,
    trimmedName,
    savedName,
    isGenderChanged,
    gender,
    pickedImage,
    setUser,
    close,
    user?.id,
  ]);

  /** Drops every unsaved edit — including a picture already in the bucket. */
  const handleCancel = useCallback(() => {
    close();
  }, [close]);

  const handleSubmit = useCallback(async () => {
    if (
      !isSubmitting &&
      name.trim() &&
      (gender === 'male' || gender === 'female')
    ) {
      try {
        setIsSubmitting(true);
        await ParticipantsService.updateCurrentParticipantProfile({
          name: name.trim(),
          gender,
          ...(languageOptions?.length && { profileLanguage: selectedLanguage }),
        });

        // The gender-based default avatar is resolved server-side and only
        // served once a gender is set, and the PATCH response does not carry
        // it — so the profile is re-read here. Without it the avatar step
        // would offer no default avatar for the rest of the session.
        // Kept in its own try: the profile is already saved at this point, so
        // a failed re-read must not stop the modal from closing and leave the
        // user blocked behind it.
        let defaultAvatar: IUserAvatar | null | undefined;

        try {
          const profile =
            await ParticipantsService.getCurrentParticipantProfile();

          defaultAvatar = profile?.defaultAvatar ?? null;
        } catch (e) {
          console.error(
            `Failed to re-read profile after completion, default avatar stays unresolved: ${e}`,
            { participantId: user?.id },
          );
        }

        setUser((prevState: IUser) => ({
          ...prevState,
          name: name.trim(),
          gender,
          ...(defaultAvatar !== undefined && { defaultAvatar }),
        }));

        close();

        if (pathname === ROUTES.HOME_PAGE || pathname.includes(ROUTES.LOGIN)) {
          navigate(ROUTES.SELECT_EVENT);
        }
      } catch (e) {
        console.error('Error updating profile:', e);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [
    name,
    gender,
    isSubmitting,
    setUser,
    close,
    pathname,
    navigate,
    selectedLanguage,
    user?.id,
  ]);

  if (!open) {
    return null;
  }

  return (
    <ProfileCompletionModalView
      open={open}
      mode={mode}
      name={name}
      gender={gender}
      profileImageUrl={pickedImage?.url ?? user?.profileImage?.url}
      onSubmit={handleSubmit}
      onClose={handleCancel}
      onNameChange={setName}
      onGenderChange={setGender}
      onImagePick={handleImagePick}
      onSave={handleSave}
      canSave={isDirty && !!trimmedName && !isSaving && !isUploading}
      isSubmitting={isSubmitting}
      isUploading={isUploading}
      isSaving={isSaving}
      saveFailed={saveFailed}
      imageError={imageError}
      languageOptions={languageOptions}
      language={selectedLanguage}
      onLanguageChange={selectLanguage}
    />
  );
};

export default ProfileCompletionModal;
